import graphql from "babel-plugin-relay/macro";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { accountMessages } from "domain/account/accountMessages";
import { AddressFieldsQuery } from "generatedQueries/AddressFieldsQuery.graphql";
import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { Col, Row } from "reactstrap";
import { FormLabel } from "shared/components/formInputs/FormLabel";
import { FormSelect } from "shared/components/newFormInputs/FormSelect";
import { TextBox } from "shared/components/newFormInputs/TextBox";
import { Query } from "shared/components/Query";
import { ISelectOption } from "shared/components/ReactSelect";
import { sharedMessages } from "shared/sharedMessages";
import { combine, maxLength, required } from "shared/validations/validations";

interface IProps {
    namePrefix: string;
    displayPoBoxFields?: boolean;
    onPostcodeChange?: (value: string) => void;
    onStateChange?: (value: string) => void;
    onSuburbChange?: (value: string) => void;
}

export const AddressFields: FC<IProps> = ({
    namePrefix,
    displayPoBoxFields,
    onPostcodeChange,
    onStateChange,
    onSuburbChange,
}) => {
    const { formatMessage } = useIntl();
    const { control, register } = useFormContext();

    return (
        <Row form={true} data-testid="AddressFields">
            {
                displayPoBoxFields &&
                <>
                    <Col md={7}>
                        <FormLabel
                            message={accountMessages.deliveryTypeLabel}
                            required={true}
                            for={`${namePrefix}.deliveryTypeId`}
                        >
                            <Query<AddressFieldsQuery>
                                query={graphql`
                                    query AddressFieldsQuery {
                                        deliveryTypes {
                                            edges {
                                                node {
                                                    deliveryTypeId
                                                    name
                                                }
                                            }
                                        }
                                    }`}
                                variables={{}}>
                                {({ error, props }) => {
                                    if (error) {
                                        return <ErrorMessage message={sharedMessages.requestFailedBody} heading={sharedMessages.requestFailedTitle} />;
                                    }

                                    const options: ISelectOption[] = (props && props.deliveryTypes && props.deliveryTypes.edges && props.deliveryTypes.edges.filter(d => !!d.node.name).map(d => {
                                        return {
                                            label: d.node.name!,
                                            value: d.node.deliveryTypeId,
                                        };
                                    })) || [];

                                    return <FormSelect
                                        name={`${namePrefix}.deliveryTypeId`}
                                        testId={`${namePrefix}-delivery-type`}
                                        options={options}
                                        placeholder={formatMessage(accountMessages.deliveryTypeLabel)}
                                        isSearchable={false}
                                        isLoading={!props}
                                        rules={required()}
                                        aria-required={true} />;
                                }}
                            </Query>
                        </FormLabel>
                    </Col>
                    <Col md={5}>
                        <FormLabel
                            message={accountMessages.deliveryNumberLabel}
                            required={true}
                            for={`${namePrefix}.deliveryNumber`}
                        >
                            <TextBox
                                id={`${namePrefix}.deliveryNumber`}
                                name={`${namePrefix}.deliveryNumber`}
                                control={control}
                                register={register}
                                placeholder={formatMessage(accountMessages.deliveryNumberLabel)}
                                options={combine(maxLength(10), required())}
                                aria-required={true}
                            />
                        </FormLabel>
                    </Col>
                </>
            }
            {
                !displayPoBoxFields &&
                <Col md={12}>
                    <FormLabel
                        message={accountMessages.addressLineLabel}
                        required={true}
                        for={`${namePrefix}.addressLine`}
                    >
                        <TextBox
                            id={`${namePrefix}.addressLine`}
                            name={`${namePrefix}.addressLine`}
                            control={control}
                            register={register}
                            placeholder={formatMessage(accountMessages.addressLineLabel)}
                            options={combine(required(), maxLength(500))}
                            aria-required={true}
                        />
                    </FormLabel>
                </Col>
            }
            <Col md={5}>
                <FormLabel
                    message={accountMessages.suburbLabel}
                    required={true}
                    for={`${namePrefix}.suburb`}
                >
                    <TextBox
                        id={`${namePrefix}.suburb`}
                        name={`${namePrefix}.suburb`}
                        control={control}
                        register={register}
                        placeholder={formatMessage(accountMessages.suburbLabel)}
                        options={{
                            ...combine(required(), maxLength(100)),
                            onChange: onSuburbChange,
                        }}
                        aria-required={true}
                    />
                </FormLabel>
            </Col>
            <Col md={4}>
                <FormLabel
                    message={accountMessages.stateLabel}
                    required={true}
                    for={`${namePrefix}.state`}
                >
                    <TextBox
                        id={`${namePrefix}.state`}
                        name={`${namePrefix}.state`}
                        control={control}
                        register={register}
                        placeholder={formatMessage(accountMessages.stateLabel)}
                        options={{
                            ...combine(required(), maxLength(50)),
                            onChange: onStateChange,
                        }}
                        aria-required={true}
                    />
                </FormLabel>

            </Col>
            <Col md={3}>
                <FormLabel
                    message={accountMessages.postcodeLabel}
                    required={true}
                    for={`${namePrefix}.postcode`}
                >
                    <TextBox
                        id={`${namePrefix}.postcode`}
                        name={`${namePrefix}.postcode`}
                        control={control}
                        register={register}
                        placeholder={formatMessage(accountMessages.postcodeLabel)}
                        options={{
                            ...combine(required(), maxLength(10)),
                            onChange: onPostcodeChange,
                        }}
                        aria-required={true}
                    />
                </FormLabel>
            </Col>
        </Row>
    );
};
